import React, { forwardRef, useState } from 'react'
import {
  Card as MuiCard,
  CardContent,
  Box,
  Typography,
  colors,
  Link,
  Avatar,
  Button,
} from '@mui/material'
import { ItemTypes } from '../utils/dnd/constants'
import { useDrag } from 'react-dnd'
import BigDecimal from 'js-big-decimal'
import { useNavigate } from 'react-router-dom'
import { grey, yellow } from '@mui/material/colors'
import TaskTypeSelectorModal from '../dashboard/TaskTypeSelectorModal'
import { AllTaskModals } from '../dashboard/Tasks'
import { TASK_TYPES } from '../constants'
import useAlert from '../hooks/useAlert'

const CardLine = ({ content, bold }) => {
  if (!content) return null

  return (
    <Typography
      sx={{
        fontSize: 12,
        maxWidth: 'inherit',
        textOverflow: 'inherit',
        overflow: 'inherit',
      }}
    >
      {bold ? <b>{content}</b> : content}
    </Typography>
  )
}

const Card = forwardRef(
  (
    {
      card,
      hideCustomer = false,
      products,
      billingCycleTypes,
      identities,
      isDragging = false,
      selectedPipeline,
      setEditingOpportunity,
      avatarUrlsMap,
    },
    ref
  ) => {
    const [currentOpenModal, setCurrentOpenModal] = useState(null)
    const [currentTask, setCurrentTask] = useState(null)
    const [currentOpenModalStaging, setCurrentOpenModalStaging] = useState(
      TASK_TYPES.PHONE_CALL
    )

    const [selectedCustomer, setSelectedCustomer] = useState(card.customer)
    const [selectedContact, setSelectedContact] = useState(null)

    const navigate = useNavigate()

    const getColor = (importanceTypeId) => {
      return {
        1: grey[100],
        2: yellow[200],
        3: '#d90202',
      }[importanceTypeId]
    }

    const { customer, accountOwnerId, accountOwners, customerAccountOwners } =
      card
    const primaryAccountOwner = accountOwners?.length
      ? accountOwners.find((ao) => ao.isPrimary) || accountOwners[0]
      : customerAccountOwners?.length
      ? customerAccountOwners.find((ao) => ao.isPrimary) ||
        customerAccountOwners[0]
      : identities.find((i) => i.id === accountOwnerId)

    const productNames =
      card.products?.map((p) => {
        const productType = products.find((pt) => pt.id === p.productId)
        return productType?.name
      }) || []

    const getMonthlyRevenue = () => {
      if (!card.products?.length) return 0
      return card.products
        .reduce((acc, product) => {
          const productType = products.find((p) => p.id === product.productId)

          if (productType?.billingCycleTypeId === 1) return acc
          const billingCycleType = billingCycleTypes.find(
            (bt) => bt?.id === productType?.billingCycleTypeId
          )

          const price = new BigDecimal(product.price || productType.price)
            .multiply(new BigDecimal(product.quantity || 1))
            .divide(new BigDecimal(billingCycleType?.ratioToMonth || 1))

          return acc.add(price)
        }, new BigDecimal(0))
        .round(2)
        .getPrettyValue('3', ',')
    }

    const getOneTimeRevenue = () => {
      if (!card.products?.length) return 0
      return card.products
        .reduce((acc, product) => {
          const productType = products.find((p) => p.id === product.productId)

          if (productType?.billingCycleTypeId !== 1) return acc

          const price = new BigDecimal(
            product.price || productType.price
          ).multiply(new BigDecimal(product.quantity || 1))

          return acc.add(price)
        }, new BigDecimal(0))
        .round(2)
        .getPrettyValue('3', ',')
    }

    const monthlyRevenue = getMonthlyRevenue()
    const oneTimeRevenue = getOneTimeRevenue()

    const isTicket = selectedPipeline.id === '__TICKETS__'
    const isOpportunity = selectedPipeline.id === '__OPPORTUNITIES__'

    const handleDoubleClick = (card) => {
      if (isOpportunity) {
        setEditingOpportunity(card)
      }
    }

    const { createAlert } = useAlert()

    const avatarUrl = primaryAccountOwner
      ? avatarUrlsMap[primaryAccountOwner.id]
      : null

    return (
      <>
        <MuiCard
          onDoubleClick={() => handleDoubleClick(card)}
          ref={ref}
          sx={{
            m: 1,
            cursor: 'pointer',
            opacity: isDragging ? 0 : 1,
            '&:hover': {
              backgroundColor: colors.grey['100'],
            },
          }}
        >
          <CardContent
            sx={{ padding: '8px', '&:last-child': { paddingBottom: '8px' } }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    backgroundColor: getColor(card.importanceType?.id),
                    width: 16,
                    marginBottom: '-8px',
                    marginTop: '-8px',
                    marginLeft: '-8px',
                    marginRight: '12px',
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: '150px',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                >
                  {isTicket && (
                    <Box sx={{ mb: 1 }}>
                      <Link
                        href={`/customers/${customer.id}?ticketId=${card.id}`}
                        variant={'text'}
                        sx={{ fontSize: '15px' }}
                      >
                        <b>Ticket #{card.id}</b>
                      </Link>
                      <CardLine content={card.categoryName} />
                    </Box>
                  )}
                  {!hideCustomer && (
                    <Link
                      href={`/customers/${customer.id}`}
                      variant={'text'}
                      sx={{ fontSize: '15px' }}
                    >
                      <b>{customer?.name}</b>
                    </Link>
                  )}

                  <Box
                    sx={{
                      fontSize: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      maxWidth: 'inherit',
                      textOverflow: 'inherit',
                      whiteSpace: 'inherit',
                      overflow: 'inherit',
                    }}
                  >
                    {(card.statusTypeId === 2 || card.statusTypeId === 3) && (
                      <>
                        <CardLine
                          bold
                          content={
                            card.statusTypeId === 2
                              ? 'Won'
                              : 'Lost' +
                                ` Reason: ${card.outcomeReasonName || '(None)'}`
                          }
                        />
                      </>
                    )}

                    <CardLine content={card.industryName} />
                    <CardLine content={productNames.join(', ')} />
                    <CardLine content={card.nextStepName} />
                    <CardLine content={card.leadSourceName} />

                    <Button
                      sx={{ width: '80px', mt: 0.5 }}
                      size='small'
                      variant='outlined'
                      onClick={() => setCurrentOpenModal('selector')}
                    >
                      Add Task
                    </Button>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                }}
              >
                {isOpportunity && (
                  <>
                    <Box>
                      {card.percentToClose}%{' > '}$
                      {card.normalizedRevenue || 0}
                    </Box>

                    {card.daysToClose && (
                      <Box>{card.daysToClose} days to close</Box>
                    )}
                  </>
                )}

                {primaryAccountOwner && (
                  <Avatar
                    src={avatarUrl}
                    sx={{ my: 1 }}
                    alt={
                      primaryAccountOwner
                        ? primaryAccountOwner.firstName +
                          primaryAccountOwner.lastName
                        : 'None'
                    }
                  >
                    {primaryAccountOwner &&
                      primaryAccountOwner.firstName[0] +
                        primaryAccountOwner.lastName[0]}
                  </Avatar>
                )}
              </Box>
            </Box>
          </CardContent>
        </MuiCard>

        <AllTaskModals
          currentOpenModal={currentOpenModal}
          currentOpenModalStaging={currentOpenModalStaging}
          setCurrentOpenModal={setCurrentOpenModal}
          setCurrentOpenModalStaging={setCurrentOpenModalStaging}
          selectedContact={selectedContact}
          setSelectedContact={setSelectedContact}
          selectedCustomer={selectedCustomer}
          setSelectedCustomer={setSelectedCustomer}
          updateTaskInList={() => {}}
          currentTask={currentTask}
          onClose={() => {
            createAlert({ message: 'Successfully created task' })
          }}
        />
      </>
    )
  }
)

const DragWrapper = ({ preventDrag = false, ...props }) => {
  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: props.isOpportunity ? ItemTypes.OPPORTUNITY : ItemTypes.CARD,
      canDrag: !preventDrag,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
      item: props.card,
    }),
    [props.card]
  )

  return (
    <Box
      style={{
        '-webkit-user-select': 'none',
        '-webkit-touch-callout': 'none',
        'user-select': 'none',
      }}
      sx={{
        borderRadius: '5px',
      }}
    >
      <Card {...props} isDragging={isDragging} ref={dragRef} />
    </Box>
  )
}

export default DragWrapper
